import { api } from './client';
import type { AiExtractResult, Payee } from './payees';

/**
 * 🔴 金额在整条链路上都是**字符串**，前端也不例外。
 * 任何 `Number(amount)` 都会把它推进 IEEE754 双精度，1234567.89 可能变成
 * 1234567.8899999999 —— 而这个值会被原样发给银行。展示、传输、比较一律用字符串。
 */

export type ApplicationStatus = 'PENDING' | 'PROCESSING' | 'PAID' | 'FAILED' | 'REJECTED';
export type PayoutStatus = 'INITIATING' | 'PROCESSING' | 'PAID' | 'FAILED' | 'CANCELLED' | 'UNKNOWN';

export interface PayoutApplication {
  id: string;
  payeeId: string;
  payeeName: string | null;
  payeeNickname: string | null;
  /** 收款人的 8 位短码，表格里直接显示，也能直接粘进搜索框。 */
  payeeShortId: string;
  /** 收款人的汇款方式（LOCAL / SWIFT）。决定确认弹窗给不给「SWIFT 中转行费用」开关。 */
  payeeTransferMethod: string | null;
  amount: string;
  currency: string;
  reason: string;
  reference: string;
  note: string | null;
  status: ApplicationStatus;
  createdByName: string | null;
  createdAt: string;
  /** 手续费承担方，创建申请时定死，财务打款时不可改。 */
  feePaidBy: FeePayer;
  /** 仅 SWIFT 收款人可能有值；LOCAL 收款人恒为 null。 */
  swiftChargeOption: string | null;
  /** 仅 REJECTED 状态有值。 */
  rejectionReason: string | null;
  /** 仅 REJECTED 状态有值：谁拒绝的。 */
  rejectedByName: string | null;
  /**
   * 这条申请对应的打款记录。还没执行过为 null。
   * 至多一条 —— `payouts.application_id` 上的唯一约束就是四道闸的第 ② 道。
   */
  payout: ApplicationPayout | null;
}

/**
 * 申请详情里带出来的打款记录摘要。
 *
 * 🔴 `status` 与 `paidAt` 必须成对使用（`payoutStatusView`）：只看 status 的话，
 * 「到账后被退回」会显示成「已取消」，而这两件事对财务的含义完全不同。
 */
export interface ApplicationPayout {
  id: string;
  status: PayoutStatus;
  providerStatus: string | null;
  shortReferenceId: string | null;
  failureReason: string | null;
  /** 上游明确拒绝时的原文，与打款记录页看到的是同一份。 */
  failureDetail: PayoutFailureDetail | null;
  receipt: ReceiptView;
  executedByName: string | null;
  createdAt: string;
  paidAt: string | null;
  /** 🔴 `amount` 为 null = 上游还没给出费用，不是「不收费」。 */
  fee: { amount: string | null; beneficiaryReceives: string | null };
}

/** 上游拒绝的原文。字段是后端白名单挑出来的，不含任何账号值。 */
/**
 * 转账确认函这一列的状态。
 *
 * 🔴 这一列只反映**上游有没有这笔 transaction**，与打款走到哪一步无关 ——
 * Airwallex 在转账仍在途时就能开出确认函，所以 `ARCHIVED` 不等于已到账。
 *
 * - `ARCHIVED`：已归档，可以看、可以下载
 * - `WAITING`：还没产生转账，确认函无从取起
 * - `GENERATING`：已有转账，正在取
 * - `REJECTED`：被 Airwallex 当场拒收、**没有产生转账**，所以不存在确认函
 * - `UNAVAILABLE`：有转账但确认函取不到，已停止重试
 */
export type ReceiptView = 'ARCHIVED' | 'WAITING' | 'GENERATING' | 'REJECTED' | 'UNAVAILABLE';

export interface PayoutFailureDetail {
  upstreamStatus: number;
  upstreamCode: string | null;
  message: string;
  fields: string[];
  rejectedAt: string;
}

/**
 * 打款记录里的手续费信息。
 *（`FeePayer` / `SwiftChargeOption` 定义在本文件下方 `executePayout` 附近，
 * 类型别名允许在声明之前引用。）
 * 🔴 `amount` 为 null 表示上游还没给出费用结果 —— 不是「不收费」，别当 0 处理。
 */
export interface PayoutFee {
  paidBy: FeePayer;
  swiftChargeOption: string | null;
  amount: string | null;
  beneficiaryReceives: string | null;
}

export interface Payout {
  id: string;
  applicationId: string;
  /** 收款人主键。列表不显示它，详情抽屉靠它取这个收款人的完整收款信息。 */
  payeeId: string;
  payeeName: string | null;
  payeeNickname: string | null;
  /** 收款人的 8 位短码，表格里直接显示，也能直接粘进搜索框。 */
  payeeShortId: string;
  /** 以下四项来自这笔打款所依据的**申请**：为什么要打这笔钱、谁提的、什么时候提的。 */
  reference: string;
  note: string | null;
  applicationCreatedByName: string | null;
  applicationCreatedAt: string;
  amount: string;
  currency: string;
  status: PayoutStatus;
  providerStatus: string | null;
  shortReferenceId: string | null;
  failureReason: string | null;
  /**
   * 失败时上游到底说了什么 —— 给研发排查用的结构化原文。
   * 只有「被 Airwallex 明确拒绝」的失败才有（见 PRODUCT.md §5.5）。
   */
  failureDetail: PayoutFailureDetail | null;
  /**
   * 确认函这一列该显示什么，由服务端算好（见后端 `receiptView`）。
   * 🔴 别在前端从 status + hasReceipt 自己推 —— 推不出「这笔永远不会有确认函」。
   */
  receipt: ReceiptView;
  executedByName: string | null;
  createdAt: string;
  /**
   * 第一次看到上游回 PAID 的时刻；从未 PAID 过为 null。
   * 🔴 `status === 'CANCELLED' && paidAt` = 到账后被退回，见 domain/payouts.ts 的 payoutStatusView。
   */
  paidAt: string | null;
  fee: PayoutFee;
}

/**
 * 🔴 金额区间的两端是**字符串**。写成 number 的那一刻精度就已经丢了，
 * 而丢了精度的边界会静静地筛掉一笔本该被看到的记录。
 */
export interface ApplicationFilter {
  keyword?: string;
  status?: ApplicationStatus[];
  currency?: string[];
  amountMin?: string;
  amountMax?: string;
  createdFrom?: string;
  createdTo?: string;
}

export interface PayoutFilter {
  keyword?: string;
  status?: PayoutStatus[];
  currency?: string[];
  amountMin?: string;
  amountMax?: string;
  hasReceipt?: boolean;
  /**
   * 只看 / 排除「到账后被退回」的记录。
   * 🔴 状态筛选分不出它：它和「没打成就被取消」在库里都是 `CANCELLED`，
   * 区别只在有没有 `paidAt`。
   */
  returned?: boolean;
  createdFrom?: string;
  createdTo?: string;
}

export async function listApplications(filter: ApplicationFilter = {}) {
  const { data } = await api.post<{
    items: PayoutApplication[];
    facets: { currencies: string[] };
  }>('/payout-applications/list', filter);
  return data;
}

/** 手续费承担方：谁付 Airwallex 收的那笔手续费。 */
export type FeePayer = 'PAYER' | 'BENEFICIARY';
/** SWIFT 中转行费用怎么分摊。只有 SWIFT 收款人才用得到这个字段。 */
export type SwiftChargeOption = 'PAYER' | 'SHARED';

/**
 * `/payout-applications/create` 的请求体形状。
 *
 * 🔴 `swiftChargeOption` 是可选键，且**只能在收款人是 SWIFT 时携带这个键**——
 * 非 SWIFT 收款人传了它会被后端 400（`SWIFT_OPTION_NOT_APPLICABLE`）拒绝，body 是
 * `additionalProperties: false`。调用方必须在非 SWIFT 时让这个键彻底不出现，
 * 而不是传值为 `undefined` 的键。构造这个对象的唯一入口是
 * `domain/payouts.ts` 的 `buildApplicationPayload`。
 */
export interface CreateApplicationInput {
  payeeId: string;
  amount: string;
  reference: string;
  note?: string;
  feePaidBy: FeePayer;
  swiftChargeOption?: SwiftChargeOption;
}

export interface AiApplicationResult {
  sourceRecordId?: string;
  payee: AiExtractResult;
  values: {
    amount?: string;
    reference: string;
    note?: string;
    feePaidBy: FeePayer;
    swiftChargeOption: SwiftChargeOption;
  };
  currency: string | null;
  sources: Record<string, 'input' | 'default'>;
  missing: Array<{ key: 'amount'; label: string }>;
  warnings: string[];
}

export const APPLICATION_AI_MAX_CHARS = 20_000;

/** AI 只生成待确认的申请表单；这个接口不会创建申请，更不会执行打款。 */
export async function aiExtractApplication(text: string, sourceRecordId?: string) {
  const { data } = await api.post<AiApplicationResult>('/payout-applications/ai-extract', { text, sourceRecordId });
  return data;
}

export interface CreateAiApplicationInput {
  sourceRecordId?: string;
  payeeValues: Record<string, string>;
  amount: string;
  reference?: string;
  note?: string;
  feePaidBy: FeePayer;
  swiftChargeOption?: SwiftChargeOption;
}

/** 原子创建 AI 解析出的新 SWIFT 收款人与申请；不会执行打款。 */
export async function createAiApplication(input: CreateAiApplicationInput) {
  const { data } = await api.post<{ payee: Payee; application: PayoutApplication }>(
    '/payout-applications/ai-create',
    input,
  );
  return data;
}

export interface AiBatchWorkbookColumn {
  key: string;
  title: string;
  excelColumn: string;
}

export interface AiBatchWorkbookRow {
  id: string;
  rowNumber: number;
  cells: Record<string, string | null>;
  unsafeCells: Array<{ key: string; reason: 'UNSAFE_NUMBER' }>;
  rowGroupingUncertain: boolean;
  warnings: string[];
}

export interface AiBatchWorkbookResult {
  sourceRecordId?: string;
  token: string;
  sheetName: string;
  headerDetected: boolean;
  headerConfidence: 'high' | 'low';
  firstNonEmptyRowNumber: number;
  columns: AiBatchWorkbookColumn[];
  rows: AiBatchWorkbookRow[];
}

export interface AiBatchHeaderDecision {
  sourceRecordId?: string;
  firstRowIsHeader: boolean;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

export async function aiDetectBatchHeader(rows: string[][], sourceRecordId?: string) {
  const { data } = await api.post<AiBatchHeaderDecision>(
    '/payout-applications/ai-batch/detect-header',
    { rows, sourceRecordId },
  );
  return data;
}

export async function parseAiBatchWorkbook(file: File, firstRowIsHeader?: boolean) {
  const form = new FormData();
  form.append('file', file);
  const suffix = firstRowIsHeader === undefined ? '' : `?firstRowIsHeader=${String(firstRowIsHeader)}`;
  const { data } = await api.post<AiBatchWorkbookResult>(
    `/payout-applications/ai-batch/parse-workbook${suffix}`,
    form,
  );
  return data;
}

export async function issueAiBatchToken() {
  const { data } = await api.post<{ token: string }>('/payout-applications/ai-batch/token', {});
  return data.token;
}

export interface CreateAiApplicationBatchRow extends CreateAiApplicationInput {
  clientRowId: string;
  rowNumber: number;
}

export interface CreateAiApplicationBatchInput {
  sourceRecordId?: string;
  token: string;
  rows: CreateAiApplicationBatchRow[];
}

export interface CreateAiApplicationBatchResult {
  batchId: string;
  duplicated: boolean;
  created: Array<{
    clientRowId: string;
    rowNumber: number;
    payeeId: string;
    payeeShortId: string;
    applicationId: string;
  }>;
}

export async function createAiApplicationBatch(input: CreateAiApplicationBatchInput) {
  const { data } = await api.post<CreateAiApplicationBatchResult>(
    '/payout-applications/ai-batch/create',
    input,
  );
  return data;
}

export async function createApplication(input: CreateApplicationInput) {
  const { data } = await api.post<PayoutApplication>('/payout-applications/create', input);
  return data;
}

/** 财务拒绝一条申请。🔴 理由必填——申请人只能重提一条新申请，得知道该改什么。 */
export async function rejectApplication(id: string, reason: string) {
  const { data } = await api.post<{ ok: true }>('/payout-applications/reject', { id, reason });
  return data;
}

/**
 * 🔴 真实动钱。同一个申请重复调用会被服务端 409 挡住。
 *
 * 手续费承担方在申请创建时已经定死（见 §5.8），执行打款时不再接受、也不能覆盖，
 * 所以这里只传 `applicationId`。
 */
export async function executePayout(applicationId: string) {
  const { data } = await api.post<Payout>('/payouts/execute', { applicationId });
  return data;
}

export async function listPayouts(filter: PayoutFilter = {}) {
  const { data } = await api.post<{
    items: Payout[];
    facets: { currencies: string[] };
    /**
     * 🔴 未出最终结果的打款笔数，**取自全量、不受筛选影响**。
     * 顶部那条提示靠它 —— 筛一下「已到账」就让提示消失，等于让几笔状态未定的钱
     * 从界面上凭空消失。
     */
    pendingCount: number;
  /** 到账后又被退回的笔数。与 pendingCount 同样取自全量，不随筛选变化。 */
  returnedCount: number;
  }>('/payouts/list', filter);
  return data;
}

/** 手动核对单条打款的真实状态。 */
export async function refreshPayout(id: string) {
  const { data } = await api.post<{ ok: true }>('/payouts/refresh', { id });
  return data;
}

/**
 * 手动核对**全部**未完成的打款。
 * 自动对账每小时整点跑一次；不想等到下个整点时用这个。
 */
export async function checkAllPayouts() {
  const { data } = await api.post<{ checked: number }>('/payouts/check-all', {});
  return data;
}

/**
 * 取确认函 PDF 的字节。
 *
 * 🔴 **为什么不是拿一个地址让浏览器去开**：COS 那个桶开着「强制下载」，响应头恒为
 * `Content-Disposition: attachment`，签名 URL 上带 `response-content-disposition=inline`
 * 也覆盖不掉（都是对真实对象实测的）。浏览器拿到那个地址只会下载、预览不了，
 * 而桶与 agency-crm 共用，改桶设置会波及那边。所以字节由后端代出来。
 *
 * 用 axios 而不是裸 fetch：要复用它的鉴权拦截器与统一错误信封 —— 这个接口会回
 * `RECEIPT_UNAVAILABLE` / `RECEIPT_NOT_READY` 这类业务错误，得能被 `apiErrorMessage` 认出来。
 * 所以 `responseType: 'blob'` 之外还要把错误体从 Blob 转回 JSON，见 client.ts 的说明。
 */
export async function receiptFile(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await api.post('/payouts/receipt-file', { id }, { responseType: 'blob' });
  const blob = res.data as Blob;
  // 文件名以服务端给的为准（它用的是 Airwallex 短单号，跟客服对账时手上就是这串）
  const disposition = String(res.headers['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `transfer-confirmation-${id}.pdf`;
  return { blob, filename };
}
